import type * as AccordionPrimitive from "@radix-ui/react-accordion";
import type {
  ComponentPropsWithoutRef,
  ComponentRef,
  HTMLAttributes,
  ReactNode,
} from "react";

import type { TIconKey } from "@/ui/components/primitives/Icon";
import type { DefinedDefaults } from "@/ui/lib";

/*-------------------------------------
|  Constants
-------------------------------------*/

export const ACCORDION_TRIGGER_ICON: TIconKey = "chevronDown";

/*-------------------------------------
|  Types
-------------------------------------*/

export type TAccordionItem = {
  content: ReactNode;
  disabled?: boolean;
  label: ReactNode;
  value: string;
};

export type TAccordionRootProps = Extract<
  ComponentPropsWithoutRef<typeof AccordionPrimitive.Root>,
  { type: "single" }
>;
export type TAccordionItemElement = ComponentRef<typeof AccordionPrimitive.Item>;
export type TAccordionItemProps = ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>;
export type TAccordionHeaderElement = ComponentRef<typeof AccordionPrimitive.Header>;
export type TAccordionHeaderProps = ComponentPropsWithoutRef<typeof AccordionPrimitive.Header>;
export type TAccordionTriggerElement = ComponentRef<typeof AccordionPrimitive.Trigger>;
export type TAccordionTriggerProps = ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger>;
export type TAccordionTriggerIconProps = HTMLAttributes<HTMLSpanElement>;
export type TAccordionContentElement = ComponentRef<typeof AccordionPrimitive.Content>;
export type TAccordionContentProps = ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>;

export type TAccordionProps = Omit<TAccordionRootProps, "children" | "type"> & {
  contentClassName?: string;
  items: TAccordionItem[];
  triggerClassName?: string;
  type?: "single";
} & Pick<HTMLAttributes<HTMLElement>, "className">;

type TAccordionDefaultProps = DefinedDefaults<
  TAccordionProps,
  "type" |
  "collapsible"
>;

/*-------------------------------------
|  Defaults
-------------------------------------*/

export const ACCORDION_DEFAULTS = {
  type: "single",
  collapsible: true,
} satisfies TAccordionDefaultProps;
